/**
 * <s-product-image-zoom> — PDP full-screen photo gallery.
 *
 * Every gallery button opens the one #product-image-zoom dialog declaratively (commandfor/show-modal).
 * This component:
 *   - scrolls the dialog's track to the photo whose button opened it (nth [data-zoom-preload] = nth slide);
 *   - handles the custom commands --zoom-prev, --zoom-next and --zoom-goto (value = slide index);
 *   - keeps the counter, arrows' disabled state and thumbnail aria-current in sync while swiping;
 *   - moves with ArrowLeft/ArrowRight while open;
 *   - warms the matching high-resolution image on hover/focus of a gallery button.
 *
 * Markup:
 *   [data-zoom-preload]                      — gallery trigger buttons, in slide order
 *   dialog[data-zoom-dialog] [data-zoom-track] > [data-zoom-slide] img[data-zoom-image]
 *   [data-zoom-prev] [data-zoom-next] [data-zoom-thumb] [data-zoom-counter] — optional, only with 2+ photos
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #dialog = null;
  #track = null;
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#dialog = this.querySelector('dialog[data-zoom-dialog]');
    this.#track = this.#dialog?.querySelector('[data-zoom-track]') ?? null;

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    if (!this.#dialog || !this.#track) return;

    // CommandEvent does not bubble, so listen on the dialog it targets.
    this.#dialog.addEventListener('command', this.#handleCommand, opts);
    this.#dialog.addEventListener('keydown', this.#handleKeydown, opts);
    this.#track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  get #slides() {
    return this.#track ? [...this.#track.querySelectorAll('[data-zoom-slide]')] : [];
  }

  get #triggers() {
    return [...this.querySelectorAll('[data-zoom-preload]')];
  }

  #handleCommand = (event) => {
    const { command, source } = event;

    if (command === 'show-modal') {
      const index = Math.max(0, this.#triggers.indexOf(source));
      // Wait for the dialog to lay out before jumping, so the track has its width.
      requestAnimationFrame(() => this.#goTo(index, 'instant'));
      return;
    }

    if (command === '--zoom-prev') this.#goTo(this.#index - 1);
    else if (command === '--zoom-next') this.#goTo(this.#index + 1);
    else if (command === '--zoom-goto') this.#goTo(Number(source?.value) || 0);
  };

  #handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const forward = (event.key === 'ArrowRight') !== (getComputedStyle(this.#dialog).direction === 'rtl');
    event.preventDefault();
    this.#goTo(this.#index + (forward ? 1 : -1));
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.#track.clientWidth || 1;
      this.#sync(Math.round(Math.abs(this.#track.scrollLeft) / width));
    });
  };

  #goTo(index, behavior = 'smooth') {
    const slides = this.#slides;
    if (slides.length === 0) return;
    const target = Math.min(Math.max(index, 0), slides.length - 1);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const image = slides[target].querySelector('img');
    if (image) image.loading = 'eager';
    slides[target].scrollIntoView({ behavior: reduceMotion ? 'instant' : behavior, block: 'nearest', inline: 'center' });
    this.#sync(target);
  }

  #sync(index) {
    const slides = this.#slides;
    if (slides.length === 0) return;
    this.#index = Math.min(Math.max(index, 0), slides.length - 1);

    const counter = this.#dialog.querySelector('[data-zoom-counter]');
    if (counter) counter.textContent = `${this.#index + 1} / ${slides.length}`;

    const prev = this.#dialog.querySelector('[data-zoom-prev]');
    const next = this.#dialog.querySelector('[data-zoom-next]');
    if (prev) prev.disabled = this.#index === 0;
    if (next) next.disabled = this.#index === slides.length - 1;

    this.#dialog.querySelectorAll('[data-zoom-thumb]').forEach((thumb, i) => {
      if (i === this.#index) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(this.#triggers.indexOf(source));
  };

  #preload(index) {
    if (index < 0 || this.#preloaded.has(index)) return;

    const image = this.#slides[index]?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(index);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
